// pages/ProductComparisonResultPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  postProductCompare,
  getScannerPrefs,
  type ProductCompareResponse,
  type ProductPairIds,
} from '../api/endpoints';
import { isIngredientsPendingError } from '../api/pairErrors';
import { createProductReference, type ProductReference } from '../utils/productIdentity';
import { API_BASE_URL } from '../api/config';
import useFavoriteProduct from '../hooks/useFavoriteProduct';
import { motion, useReducedMotion } from 'framer-motion';
import { postScannerCompare } from '../api/telemetry';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { getErrorMessage } from '../utils/errors';
import {
  comedogenicScore,
  getIrritantLevel,
  ingredientDisplayName,
  labelize,
  shareOrCopyUrl,
} from '../utils/scannerIngredient';
import type { ProductAnalysisSnapshot, ScannerIngredient } from '../types/product';
import { knownBreakdownEntries } from '../utils/scoreBreakdown';
import {
  IconScale,
  IconArrow,
  IconBadge,
  IconShare,
  IconHeart,
  IconBeaker,
  IconChart,
  IconWarn,
  Chip,
  Badge,
  SectionCard,
  Stat,
  ScoreRing,
} from './productComparisonResultAtoms';

/* ==========================================================
   Helpers
   ========================================================== */
const normalizeImage = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  try {
    const absolute = new URL(url.startsWith('/') ? url : `/${url}`, API_BASE_URL);
    return absolute.toString();
  } catch { return null; }
};
const fmt = (n?: number) => (typeof n === 'number' && Number.isFinite(n) ? `${Math.round(n)}` : '—');
const prettyOutcome = (v?: 'a' | 'b' | 'tie') => (v === 'a' ? 'Product A' : v === 'b' ? 'Product B' : 'Tie');

type NavState = { response?: ProductCompareResponse; payload?: ProductPairIds; productIds?: { a?: number; b?: number } };

/** Positive integer id from a URL param, else undefined. */
function idParam(value: string | null): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** What the scan screen needs to refill a side: id, and the name when we have it. */
function sideReference(product: ProductCompareResponse['product_a'] | undefined, fallbackId?: number): ProductReference | undefined {
  const productId = product?.product_id ?? fallbackId;
  if (!productId) return undefined;
  const analysis = product ? asCompareAnalysis(product.analysis) : {};
  return createProductReference({
    productId,
    name: analysis.name,
    brand: analysis.brand,
    ...(product?.image_url ? { image_url: product.image_url } : {}),
  });
}
type PrefsOut = Awaited<ReturnType<typeof getScannerPrefs>>;
type CompareAnalysis = ProductAnalysisSnapshot;

function isCompareAnalysis(value: unknown): value is CompareAnalysis {
  return typeof value === 'object' && value !== null;
}

function asCompareAnalysis(value: unknown): CompareAnalysis {
  if (isCompareAnalysis(value)) return value;
  return {};
}

function getAnalysisIngredients(product: ProductCompareResponse['product_a'] | undefined): ScannerIngredient[] {
  return asCompareAnalysis(product?.analysis).ingredients ?? [];
}

/* ==========================================================
   Page
   ========================================================== */
export default function ProductComparisonResultPage(): React.JSX.Element {
  const nav = useNavigate();
  const state = (useLocation().state ?? {}) as NavState;
  const [params] = useSearchParams();

  console.log('[PAGE LOAD] ProductComparisonResultPage - Initial state:', {
    has_state_response: !!state.response,
    has_state_payload: !!state.payload,
    state_productIds: state.productIds,
    url_params: { a: params.get('a'), b: params.get('b') }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ProductCompareResponse | null>(state.response ?? null);
  const [payload, setPayload] = useState<ProductPairIds | null>(state.payload ?? null);
  const [productIds] = useState(state.productIds);
  const [copied, setCopied] = useState<string | null>(null);
  const [telemetrySent, setTelemetrySent] = useState(false);

  // prefs
  const [prefs, setPrefs] = useState<PrefsOut | null>(null);
  useEffect(() => {
    let alive = true;
    getScannerPrefs()
      .then((p) => { if (alive) setPrefs(p); })
      .catch(() => { if (alive) setPrefs(null); });
    return () => { alive = false; };
  }, []);
  const hasPrefs =
    prefs !== null &&
    (prefs.allergies.length > 0 ||
      prefs.acne_prone ||
      prefs.ethical_mode !== 'off' ||
      prefs.cautious_actives);
  const activePrefs = hasPrefs ? prefs : null;

  // Fetch comparison if not already in state. Supports barcode params (a/b) and
  // product-id params (pa/pb) so a pair can be re-opened from history — including
  // slug-only products without a barcode. Re-running an already-scanned pair is
  // free on the backend (no credits consumed).
  // A 409 (ingredients still with the team) sends the user back to the scan
  // screen with that side marked, so they can pick another product.
  const failed = useCallback((error_: unknown, fallback: string, ids: ProductPairIds | null) => {
    if (isIngredientsPendingError(error_)) {
      nav('/comparison', {
        replace: true,
        state: {
          pendingSide: error_.side,
          products: [sideReference(undefined, ids?.product_id_a), sideReference(undefined, ids?.product_id_b)],
        },
      });
      return;
    }
    setError(getErrorMessage(error_, fallback));
  }, [nav]);

  useEffect(() => {
    if (response) return;
    const pa = idParam(params.get('pa'));
    const pb = idParam(params.get('pb'));
    if (!pa || !pb) {
      if (params.get('a') || params.get('b')) setError('This link is out of date. Open the comparison again from your history.');
      return;
    }
    const p: ProductPairIds = { product_id_a: pa, product_id_b: pb };
    setPayload(p); setLoading(true);
    postProductCompare(p)
      .then((res) => {
        setResponse(res);
      })
      .catch((error_: unknown) => { failed(error_, 'Failed to load comparison', p); })
      .finally(() => { setLoading(false); });
  }, [response, params, failed]);

  // Track telemetry whenever we have a valid response (from API or navigation state)
  useEffect(() => {
    console.log('[TELEMETRY USEEFFECT] Scanner Compare useEffect triggered:', {
      has_response: !!response,
      telemetrySent,
      will_run: !!response && !telemetrySent
    });

    if (!response || telemetrySent) return;

    console.log('[TELEMETRY DEBUG] Scanner Compare Response:', {
      has_product_a: !!response.product_a,
      has_product_b: !!response.product_b,
      product_id_a: response.product_a.product_id,
      product_id_b: response.product_b.product_id,
    });

    if (response.product_a.product_id && response.product_b.product_id) {
      console.log('[TELEMETRY DEBUG] Sending scanner compare telemetry:', response.product_a.product_id, response.product_b.product_id);
      void postScannerCompare(response.product_a.product_id, response.product_b.product_id).catch((error_: unknown) => {
        console.error('[TELEMETRY ERROR] Scanner compare failed:', error_);
      });
      setTelemetrySent(true);
    } else {
      console.warn('[TELEMETRY DEBUG] Missing product IDs, cannot send scanner compare telemetry');
    }
  }, [response, telemetrySent]);

  const productA = response?.product_a;
  const productB = response?.product_b;
  const comparison = response?.comparison;

  const favoriteA = useFavoriteProduct({ productId: productA?.product_id, disabled: !productA?.product_id });
  const favoriteB = useFavoriteProduct({ productId: productB?.product_id, disabled: !productB?.product_id });

  const betterOverall = comparison?.better_overall;
  const betterForUser = comparison?.better_for_user;

  const share = async () => {
    const url = window.location.href;
    try {
      await shareOrCopyUrl('Comparison result', url, () => {
        setCopied('Link copied');
        setTimeout(() => { setCopied(null); }, 1500);
      });
    } catch {
      /* share/copy cancelled or unavailable */
    }
  };

  const retry = async () => {
    if (!payload) { nav('/comparison'); return; }
    setLoading(true); setError(null);
    try { setResponse(await postProductCompare(payload)); }
    catch (error_: unknown) { failed(error_, 'Failed to refresh comparison', payload); }
    finally { setLoading(false); }
  };

  const prefersReduced = useReducedMotion() ?? false;
  const scoreBreakdownA = productA ? asCompareAnalysis(productA.analysis).score_breakdown : undefined;
  const scoreBreakdownB = productB ? asCompareAnalysis(productB.analysis).score_breakdown : undefined;

  return (
    <>
      <div className="stack-5" style={{ minWidth: 0 }}>

        {/* Free re-scan: this pair was already in history, so no credits were used. */}
        {response?.charged === false && (
          <div className="pill pill--teal" style={{ alignSelf: 'flex-start' }}>
            <span className="dot" />
            Already scanned — no tokens used
          </div>
        )}

        {/* ── Hero ─────────────────────────────────────────── */}
        <div className="card card--bordered" style={{ position: 'relative', overflow: 'hidden', padding: 0 }}>
          <div
            className="scan-pattern"
            style={{ position: 'absolute', inset: 0, opacity: 0.3, pointerEvents: 'none' }}
          />
          <div className="stack-4" style={{ position: 'relative', padding: 'var(--s-5)' }}>

            {/* Top row: icon + actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <div
                className="icon-ring"
                style={{ width: 'var(--s-7)', height: 'var(--s-7)', flexShrink: 0 }}
              >
                <IconScale />
              </div>
              <div className="row-3" style={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={share} className="btn btn--ghost btn--sm">
                  <IconShare />Share
                </button>
                <button
                  onClick={() => { nav('/comparison', {
                    state: {
                      products: [sideReference(productA, productIds?.a), sideReference(productB, productIds?.b)],
                    }
                  }); }}
                  className="btn btn--ghost btn--sm"
                >
                  New comparison
                </button>
              </div>
            </div>

            {/* Title + description */}
            <div style={{ minWidth: 0 }}>
              <h1 className="t-h2" style={{ margin: 0 }}>
                Comparison <em className="t-italic t-teal">result.</em>
              </h1>
              <p className="t-small t-mute" style={{ margin: 'var(--s-2) 0 0' }}>
                Side-by-side ingredient intelligence. The stronger match is highlighted.
              </p>
            </div>

            {/* Outcome chips */}
            {comparison && (
              <div className="stack-2">
                <Chip tone={betterOverall === 'tie' ? 'neutral' : 'success'} block>
                  Better overall: {prettyOutcome(betterOverall)}
                </Chip>
                <Chip tone={betterForUser === 'tie' ? 'neutral' : 'success'} block>
                  Better for you: {prettyOutcome(betterForUser)}
                </Chip>
              </div>
            )}

            {/* Preference flags */}
            {activePrefs && (
              <div className="g-2">
                {activePrefs.acne_prone && <Chip tone="warn" block>Breakout-prone</Chip>}
                {activePrefs.cautious_actives && <Chip tone="warn" block>Gentle actives</Chip>}
                {activePrefs.ethical_mode !== 'off' &&
                  <Chip block>Ethical: {labelize(activePrefs.ethical_mode)}</Chip>}
                {activePrefs.allergies.length > 0 &&
                  <Chip block>
                    {activePrefs.allergies.length} allergen{activePrefs.allergies.length > 1 ? 's' : ''} selected
                  </Chip>}
              </div>
            )}
          </div>
        </div>

        {/* ── Loading ────────────────────────────────────────── */}
        {loading && (
          <div className="card card--bordered" style={{ padding: 'var(--s-5)' }}>
            <p className="t-small t-mute" style={{ margin: 0 }}>Loading comparison…</p>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────── */}
        {error && (
          <div className="card card--alert" style={{ padding: 'var(--s-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <p className="t-small" style={{ margin: 0, color: 'var(--c-coral)', minWidth: 0 }}>{error}</p>
              <button
                onClick={retry}
                className="btn btn--ghost btn--sm"
                style={{ flexShrink: 0, color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* ── Product panels ─────────────────────────────────── */}
        {productA && productB && (
          <div className="g-2" style={{ minWidth: 0 }}>
            <ProductPanel
              side="a"
              product={productA}
              barcode={productA.barcode}
              favorite={favoriteA}
              highlight={betterOverall === 'a' || betterForUser === 'a'}
            />
            <ProductPanel
              side="b"
              product={productB}
              barcode={productB.barcode}
              favorite={favoriteB}
              highlight={betterOverall === 'b' || betterForUser === 'b'}
            />
          </div>
        )}

        {/* ── Outcomes ──────────────────────────────────────── */}
        {comparison && (
          <SectionCard title="Outcomes" icon={<IconArrow />}>
            <div className="g-2">
              <OutcomeCard title="Better overall" value={prettyOutcome(betterOverall)} delta={comparison.delta_overall} />
              <OutcomeCard title="Better for you" value={prettyOutcome(betterForUser)} delta={comparison.delta_for_user} />
            </div>
          </SectionCard>
        )}

        {/* ── Score breakdown ────────────────────────────────── */}
        {(scoreBreakdownA ?? scoreBreakdownB) && (
          <SectionCard title="Score breakdown" icon={<IconChart />}>
            <div className="g-2">
              <BreakdownCard side="a" breakdown={scoreBreakdownA} prefersReduced={prefersReduced} />
              <BreakdownCard side="b" breakdown={scoreBreakdownB} prefersReduced={prefersReduced} />
            </div>
          </SectionCard>
        )}

        {/* ── Head-to-head ───────────────────────────────────── */}
        {productA && productB && (
          <SectionCard title="Head-to-head highlights" icon={<IconScale />}>
            <HeadToHead a={productA} b={productB} />
          </SectionCard>
        )}

        {/* ── Ingredients ────────────────────────────────────── */}
        {productA && productB && (
          <SectionCard title="Ingredients (full lists)" icon={<IconBeaker />}>
            <div className="g-2">
              <IngredientsAccordion side="a" ingredients={getAnalysisIngredients(productA)} />
              <IngredientsAccordion side="b" ingredients={getAnalysisIngredients(productB)} />
            </div>
          </SectionCard>
        )}
      </div>

      {/* ── Toast ──────────────────────────────────────────── */}
      {copied && (
        <div style={{ position: 'fixed', bottom: 'var(--s-6)', left: '50%', transform: 'translateX(-50%)', zIndex: 50 }}>
          <span className="pill" style={{ background: 'var(--c-charcoal)', color: 'var(--c-cream)' }}>
            {copied}
          </span>
        </div>
      )}

      <AIDisclaimerFooter />
    </>
  );
}

/* ==========================================================
   Subcomponents
   ========================================================== */
function OutcomeCard({ title, value, delta }: { title: string; value: string; delta: number }) {
  return (
    <div className="card card--sand card--bordered card--compact card--flat" style={{ minWidth: 0 }}>
      <div className="row-3 t-label" style={{ color: 'var(--ink-muted)', marginBottom: 'var(--s-2)' }}>
        <IconArrow />{title}
      </div>
      <div className="t-h3" style={{ margin: 0 }}>{value}</div>
      <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>
        Delta: {typeof delta === 'number' && Number.isFinite(delta) ? delta.toFixed(1) : '—'}
      </div>
    </div>
  );
}

function ProductPanel({
  side, product, barcode, favorite, highlight,
}: {
  side: 'a' | 'b';
  product: ProductCompareResponse['product_a'];
  barcode: string;
  favorite: { favorited: boolean | null; loading: boolean; toggle: () => void };
  highlight: boolean;
}) {
  const label = `Product ${side.toUpperCase()}`;
  const analysis = asCompareAnalysis(product.analysis);
  const imageSrc = normalizeImage(product.image_url);

  const fav = favorite.favorited;
  const ariaPressed = fav ?? undefined;
  const scoreVal = analysis.score ?? analysis.score_raw ?? 0;
  const ingredients = analysis.ingredients ?? [];

  return (
    <div
      className={['card card--bordered stack-4 compare-result-side', `compare-result-side--${side}`, highlight ? 'card--highlight' : ''].filter(Boolean).join(' ')}
      data-testid={`compare-result-product-${side}`}
      style={{ padding: 'var(--s-4)', minWidth: 0 }}
    >
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
        <span className="t-label compare-result-side__label">
          <span className="compare-result-side__marker" data-testid={`compare-side-marker-${side}`} aria-hidden />{label}
        </span>
        {highlight && (
          <span className="pill pill--teal" style={{ gap: 'var(--s-1)' }}>
            <IconBadge /> Highlighted
          </span>
        )}
      </div>

      {/* Image + score ring */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-4)', alignItems: 'center' }}>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          {imageSrc ? (
            <div
              className="frame"
              style={{ width: 140, height: 140, display: 'grid', placeItems: 'center' }}
            >
              <img
                src={imageSrc}
                alt={label}
                style={{ maxHeight: 120, maxWidth: 110, objectFit: 'contain' }}
              />
            </div>
          ) : (
            <div
              style={{
                width: 140, height: 140,
                borderRadius: 'var(--r-md)',
                border: '1.5px dashed var(--c-charcoal-30)',
                display: 'grid', placeItems: 'center',
              }}
            >
              <span className="t-small t-mute">No image</span>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <ScoreRing score={scoreVal} size={140} />
        </div>
      </div>

      {/* Product details */}
      <div style={{ minWidth: 0 }}>
        <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {analysis.name ?? 'Unknown product'}
        </div>
        <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {analysis.brand ?? 'Brand unavailable'}
        </div>

        {/* Meta chips */}
        <div className="row-3" style={{ flexWrap: 'wrap', marginTop: 'var(--s-2)' }}>
          <StatChip label="Barcode" value={barcode} />
          {typeof product.product_id === 'number' && (
            <span className="pill">ID {product.product_id}</span>
          )}
        </div>

        {/* Stats grid */}
        <div className="g-2" style={{ marginTop: 'var(--s-3)' }}>
          <Stat label="Score" value={fmt(scoreVal)} />
          <Stat label="Ingredients" value={fmt(ingredients.length)} />
        </div>

        {/* Actions */}
        <div className="stack-2" style={{ marginTop: 'var(--s-3)' }}>
          <button
            type="button"
            onClick={favorite.toggle}
            disabled={favorite.loading || !product.product_id}
            aria-pressed={ariaPressed}
            aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
            className="btn btn--ghost btn--sm btn--full"
            style={fav ? { color: 'var(--c-coral)', borderColor: 'var(--c-coral)' } : undefined}
          >
            <IconHeart filled={Boolean(fav)} />
            {fav ? 'Remove from favorites' : 'Add to favorites'}
          </button>

          {typeof product.product_id === 'number' && (
            <button
              type="button"
              onClick={() => window.open(`/favorites/${product.product_id}`, '_self')}
              className="btn btn--ghost btn--sm btn--full"
            >
              View details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="pill">
      <span className="t-mute" style={{ whiteSpace: 'nowrap' }}>{label}</span>
      {' '}
      <span style={{ fontFamily: 'ui-monospace, monospace' }}>{value}</span>
    </span>
  );
}

/* ── Score breakdown ─────────────────────────────────────── */
function BreakdownCard({
  side, breakdown, prefersReduced,
}: {
  side: 'a' | 'b';
  breakdown?: Record<string, number>;
  prefersReduced: boolean;
}) {
  const label = `Product ${side.toUpperCase()}`;
  if (!breakdown) return (
    <div className={`card card--sand card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-2)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      <p className="t-small t-mute" style={{ margin: 0 }}>No breakdown available.</p>
    </div>
  );

  const nonZero = Object.fromEntries(Object.entries(breakdown).filter(([, v]) => Math.abs(v) > 0));
  const hasAny = Object.keys(nonZero).length > 0;

  return (
    <div className={`card card--sand card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-3)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      {hasAny
        ? <Breakdown breakdown={nonZero} prefersReduced={prefersReduced} />
        : <p className="t-small t-mute" style={{ margin: 0 }}>All factors neutral.</p>}
    </div>
  );
}

function Breakdown({ breakdown, prefersReduced }: { breakdown?: Record<string, number>; prefersReduced: boolean }) {
  if (!breakdown) return null;

  const iconFor = (k: string) => {
    const n = k.toLowerCase();
    if (n.includes('irritat') || n.includes('photo')) return <IconWarn />;
    if (n.includes('active') || n.includes('bonus') || n.includes('preserv') || n.includes('fragrance')) return <IconBeaker />;
    return <IconChart />;
  };

  const entries = knownBreakdownEntries(breakdown).map(([k, v]) => {
    const isBonus = k.includes('bonus');
    const label = labelize(k.replace(/_penalty|_bonus/g, ''));
    const value = typeof v === 'number' ? v : 0;
    const tone = isBonus ? 'success' : value >= 12 ? 'danger' : value > 0 ? 'warn' : 'neutral';
    return { key: k, label, value, isBonus, tone };
  });

  const maxVal = Math.max(1, ...entries.map((e) => Math.abs(e.value)));

  return (
    <div className="stack-3" style={{ minWidth: 0 }}>
      {entries.map((e) => {
        const width = Math.min(100, Math.round((Math.abs(e.value) / maxVal) * 100));
        const barMod = e.isBonus ? '' : e.tone === 'danger' ? 'result-bar--coral' : 'result-bar--muted';
        const numColor = e.isBonus ? 'var(--c-kivo-teal)' : e.tone === 'danger' ? 'var(--c-coral)' : 'var(--ink-muted)';
        return (
          <div key={e.key} style={{ minWidth: 0 }}>
            <div className="row-3" style={{ justifyContent: 'space-between', marginBottom: 'var(--s-1)' }}>
              <div className="row-3" style={{ minWidth: 0, overflow: 'hidden' }}>
                <span style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>{iconFor(e.key)}</span>
                <span className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.label}
                </span>
              </div>
              <span className="t-small" style={{ fontWeight: 600, color: numColor, flexShrink: 0 }}>
                {e.isBonus ? '+' : '−'}{Math.abs(e.value)}
              </span>
            </div>
            <div className={`result-bar ${barMod}`} aria-hidden>
              <motion.div
                className="result-bar__fill"
                initial={{ width: prefersReduced ? `${width}%` : 0 }}
                animate={{ width: `${width}%` }}
                transition={prefersReduced ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 28 }}
              />
              <div className="result-bar__gloss" />
            </div>
          </div>
        );
      })}
      <p className="t-small t-mute" style={{ margin: 0 }}>Penalties reduce score; bonuses increase it.</p>
    </div>
  );
}

const toStringList = (v: string[] | undefined): string[] => (Array.isArray(v) ? v : []);
const uniqueItems = (x: string[], y: string[]) => x.filter((i) => !y.includes(i));

/* ── Head-to-head ────────────────────────────────────────── */
function HeadToHead({ a, b }: { a: ProductCompareResponse['product_a']; b: ProductCompareResponse['product_b'] }) {
  const A = asCompareAnalysis(a.analysis);
  const B = asCompareAnalysis(b.analysis);

  const aActives = toStringList(A.actives); const bActives = toStringList(B.actives);
  const aIrr = toStringList(A.potential_irritants); const bIrr = toStringList(B.potential_irritants);
  const aPhoto = toStringList(A.photosensitizers); const bPhoto = toStringList(B.photosensitizers);

  return (
    <div className="g-2">
      <SideHighlights side="a" label="A — Standouts" items={uniqueItems(aActives, bActives)} tone="success" empty="No unique strengths detected." />
      <SideHighlights side="b" label="B — Standouts" items={uniqueItems(bActives, aActives)} tone="success" empty="No unique strengths detected." />
      <SideHighlights side="a" label="A — Watchouts" items={[...aIrr, ...aPhoto]} tone="warn" empty="No specific cautions." />
      <SideHighlights side="b" label="B — Watchouts" items={[...bIrr, ...bPhoto]} tone="warn" empty="No specific cautions." />
    </div>
  );
}

function SideHighlights({ side, label, items, tone, empty }: { side: 'a' | 'b'; label: string; items: string[]; tone: 'success' | 'warn' | 'danger'; empty: string }) {
  return (
    <div className={`card card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 'var(--s-4)', minWidth: 0 }}>
      <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-2)' }}><span className="compare-result-side__marker" aria-hidden />{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {items.length > 0
          ? items.slice(0, 20).map((x, i) => <Chip key={`${label}-${i}`} tone={tone}>{x}</Chip>)
          : <span className="t-small t-mute">{empty}</span>}
      </div>
    </div>
  );
}

/* ── Ingredients accordion ───────────────────────────────── */
function IngredientsAccordion({ side, ingredients }: { side: 'a' | 'b'; ingredients: ScannerIngredient[] }) {
  const [open, setOpen] = useState(false);
  const count = ingredients.length;
  const label = `Product ${side.toUpperCase()}`;

  return (
    <div className={`card card--bordered card--flat compare-result-side compare-result-side--${side}`} style={{ padding: 0, overflow: 'hidden', minWidth: 0 }}>
      <button
        type="button"
        onClick={() => { setOpen((s) => !s); }}
        style={{
          display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--s-4) var(--s-5)', background: 'transparent', border: 'none', cursor: 'pointer',
        }}
        aria-expanded={open}
      >
        <div className="row-3" style={{ minWidth: 0 }}>
          <span style={{ color: 'var(--ink-muted)' }}><IconBeaker /></span>
          <span className="compare-result-side__marker" aria-hidden />
          <span className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>{label}</span>
          <span className="t-small t-mute">{count} total</span>
        </div>
        <Chevron open={open} />
      </button>

      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        style={{ overflow: 'hidden', borderTop: open ? '1px solid var(--rule)' : 'none' }}
      >
        <div style={{ padding: 'var(--s-4) var(--s-5)' }}>
          {count ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--s-2)' }}>
              {ingredients.map((ing, i) => (
                <IngredientRow key={`${ing.id ?? i}-${ing.inci_name ?? i}`} ing={ing} />
              ))}
            </ul>
          ) : (
            <p className="t-small t-mute" style={{ margin: 0 }}>No ingredients available.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function IngredientRow({ ing }: { ing: ScannerIngredient }) {
  const flags: { label: string; tone: 'success' | 'warn' | 'danger' | 'neutral' }[] = [];

  const irr = getIrritantLevel(ing);
  if (irr === 'likely')   flags.push({ label: 'Irritant (likely)',   tone: 'danger' });
  if (irr === 'possible') flags.push({ label: 'Irritant (possible)', tone: 'warn' });

  if (ing.photosensitizer)           flags.push({ label: 'Photosensitizer',    tone: 'warn' });
  if (ing.fragrance)                 flags.push({ label: 'Fragrance',          tone: 'neutral' });
  if (ing.preservative)              flags.push({ label: 'Preservative',       tone: 'neutral' });
  const comedogenic = comedogenicScore(ing);
  if (comedogenic !== null && comedogenic > 0)
    flags.push({ label: `Comedogenic ${comedogenic}/5`, tone: 'danger' });
  if (ing.active) flags.push({ label: 'Active', tone: 'success' });

  const dotClass =
    ing.active              ? 'concern-dot concern-dot--teal' :
    irr === 'likely'         ? 'concern-dot concern-dot--coral' :
    irr === 'possible'       ? 'concern-dot concern-dot--coral' :
    'concern-dot';

  return (
    <li>
      <Link
        to={`/ingredients/${ing.id ?? ''}`}
        target="_blank"
        rel="noopener noreferrer"
        className="product-result"
        style={{ padding: 'var(--s-3)' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-2)', minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ingredientDisplayName(ing)}
            </div>
            {(ing.functions?.length ?? 0) > 0 ? (
              <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(ing.functions ?? []).join(', ')}
              </div>
            ) : null}
          </div>
          <div className={dotClass} style={{ marginTop: 'var(--s-1)', flexShrink: 0 }} aria-hidden />
        </div>

        {flags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)', marginTop: 'var(--s-2)' }}>
            {flags.map((f, idx) => (
              <Badge key={idx} tone={f.tone}>{f.label}</Badge>
            ))}
          </div>
        )}
      </Link>
    </li>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18"
      aria-hidden="true"
      style={{
        color: 'var(--ink-muted)',
        flexShrink: 0,
        transition: `transform var(--t-fast) var(--ease)`,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
